import type { TLImageShape, TLShape, TLTextShape, TLVideoShape } from "@tldraw/tlschema";
import { Color } from "@tiptap/extension-color";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import StarterKit from "@tiptap/starter-kit";
import { mentionsIn, stripMentions } from "../shared/mentions";
import { ArrowShapeUtil } from "./canvas/engine/builtin/arrow";
import { DrawShapeUtil, HighlightShapeUtil } from "./canvas/engine/builtin/draw";
import { GeoShapeUtil, NoteShapeUtil } from "./canvas/engine/builtin/geo";
import { GroupShapeUtil } from "./canvas/engine/builtin/group";
import { ImageShapeUtil } from "./canvas/engine/builtin/image";
import { BookmarkShapeUtil, EmbedShapeUtil, VideoShapeUtil } from "./canvas/engine/builtin/media";
import { TextShapeUtil } from "./canvas/engine/builtin/text";
import type { ShapeUtilConstructor } from "./canvas/engine/shape-util";
import { BaseFrameLikeShapeUtil } from "./canvas/engine/shape-util";
import { DocumentUtil, TableUtil } from "./ContentShapes";
import { SceneUtil } from "./SceneShape";
import { DraftingUtil } from "./DraftingShape";
import { BoardVideo, videoPosterSvg } from "./BoardVideoShape";
import { BoardImageFrame, boardImageSvg } from "./ImageShape";
import { MentionNode } from "./MentionNode";
import { MarkUtil, SheetUtil, TraceUtil } from "./shapes";
import { textFontSize, textLineHeight } from "./text-format";

// The board's shape classes: Planroom's own shapes and the engine's
// built-ins, configured for boards.

class BoardImageUtil extends ImageShapeUtil {
  override canReceiveNewChildrenOfType(
    shape: TLImageShape,
    type: TLShape["type"],
  ) {
    return !shape.isLocked && type === "mark";
  }
  override canResizeChildren() {
    return false;
  }
  override component(shape: TLImageShape) {
    return (
      <BoardImageFrame shape={shape}>{super.component(shape)}</BoardImageFrame>
    );
  }
  override async toSvg(shape: TLImageShape) {
    return boardImageSvg(shape, await super.toSvg(shape));
  }
}

class BoardTextUtil extends TextShapeUtil {
  override extensions = [
    StarterKit,
    MentionNode,
    TextStyle,
    Color,
    TextAlign.configure({ types: ["heading", "paragraph"] }),
  ];
  override getTextStyle(shape: TLTextShape) {
    const theme = this.editor.getCurrentTheme();
    return {
      fontSize: textFontSize(shape, theme.fontSize),
      lineHeight: textLineHeight(shape, theme.lineHeight),
    };
  }
  override onBeforeCreate(shape: TLTextShape) {
    if (mentionsIn(shape.props.richText).length)
      return {
        ...shape,
        props: {
          ...shape.props,
          richText: stripMentions(shape.props.richText),
        },
      };
    // Only interactive, empty text gets new defaults. Imports and copies keep their formatting.
    if (
      !this.editor.isIn("text") ||
      JSON.stringify(shape.props.richText).includes('"text":')
    )
      return;
    return {
      ...shape,
      meta: { ...shape.meta, calqueTextSize: 12 },
      props: {
        ...shape.props,
        autoSize: false,
        w: shape.props.autoSize ? 240 : shape.props.w,
      },
    };
  }
}

class BoardVideoUtil extends VideoShapeUtil {
  override component(shape: TLVideoShape) {
    return <BoardVideo shape={shape} />;
  }
  override async toSvg(shape: TLVideoShape) {
    return videoPosterSvg(shape);
  }
}

// Reduced translucency keeps highlights over plans readable.
const BoardHighlightUtil = HighlightShapeUtil.configure({
  getCustomDisplayValues: () => ({ underlayOpacity: 0.38, overlayOpacity: 0.16 }),
});

export const boardShapes = [
  SheetUtil,
  MarkUtil,
  TraceUtil,
  DraftingUtil,
  DocumentUtil,
  TableUtil,
  SceneUtil,
  BoardImageUtil,
  BoardTextUtil,
  BoardVideoUtil,
  GroupShapeUtil,
  DrawShapeUtil,
  BoardHighlightUtil,
  GeoShapeUtil,
  NoteShapeUtil,
  ArrowShapeUtil,
  BookmarkShapeUtil,
  EmbedShapeUtil,
] as unknown as ShapeUtilConstructor[];

/** Sheets and tracing paper own their contents. */
export const isFrameLike = (util: object) =>
  util instanceof BaseFrameLikeShapeUtil;
